import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiFilter, FiX, FiChevronDown, FiChevronUp, FiSearch } from 'react-icons/fi';
import axios from '../utils/axios';
import ProductCard from '../components/ProductCard';
import { useTranslation } from 'react-i18next';
import { getLocalized } from '../utils/i18nHelpers';
import { getCategoryImage } from '../utils/imageHelpers';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/animations';

const Products = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const ITEMS_PER_PAGE = 20;
  const [showFilters, setShowFilters] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [category, setCategory] = useState(null);
  const [collection, setCollection] = useState(null);
  const [totalProducts, setTotalProducts] = useState(0);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem('mashow_current_page');
    const savedUrl = localStorage.getItem('mashow_current_url');
    if (savedUrl === window.location.search && savedPage) {
      return parseInt(savedPage, 10);
    }
    return 1;
  });

  const [filterOptions, setFilterOptions] = useState({
    materials: [],
    stoneTypes: [],
    genders: [],
    colors: []
  });

  const [expandedSections, setExpandedSections] = useState({
    search: true,
    price: true,
    type: true,
    gender: true,
    material: true,
    stone: true,
    carat: false,
    ringSize: false,
    tags: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const [filters, setFilters] = useState({
    keyword: searchParams.get('keyword') || '',
    category: searchParams.get('category') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    hasStone: searchParams.get('hasStone') || '',
    material: searchParams.get('material') || '',
    stoneType: searchParams.get('stoneType') || '',
    gender: searchParams.get('gender') || '',
    color: searchParams.get('color') || '',
    carat: searchParams.get('carat') || '',
    ringSizes: searchParams.get('ringSizes') || '',
    tags: searchParams.get('tags') || '',
    type: searchParams.get('type') || '',
    sort: searchParams.get('sort') || '-createdAt',
  });

  useEffect(() => {
    const savedUrl = localStorage.getItem('mashow_current_url');
    if (savedUrl !== window.location.search) {
      setCurrentPage(1);
    }

    fetchProducts();

    const categorySlug = searchParams.get('category');
    const collectionSlug = searchParams.get('collection');

    if (categorySlug) {
      fetchCategory(categorySlug);
      setCollection(null);
    } else if (collectionSlug) {
      fetchCollection(collectionSlug);
      setCategory(null);
    } else {
      setCategory(null);
      setCollection(null);
    }
  }, [searchParams]);

  useEffect(() => {
    // Sync filters state when URL searchParams change (to support back/forward nav)
    setFilters({
      keyword: searchParams.get('keyword') || '',
      category: searchParams.get('category') || '',
      minPrice: searchParams.get('minPrice') || '',
      maxPrice: searchParams.get('maxPrice') || '',
      hasStone: searchParams.get('hasStone') || '',
      material: searchParams.get('material') || '',
      stoneType: searchParams.get('stoneType') || '',
      gender: searchParams.get('gender') || '',
      color: searchParams.get('color') || '',
      carat: searchParams.get('carat') || '',
      ringSizes: searchParams.get('ringSizes') || '',
      tags: searchParams.get('tags') || '',
      type: searchParams.get('type') || '',
      sort: searchParams.get('sort') || '-createdAt',
    });
  }, [searchParams]);

  useEffect(() => {
    localStorage.setItem('mashow_current_page', currentPage);
    localStorage.setItem('mashow_current_url', window.location.search);
  }, [currentPage, searchParams]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setDisplayedProducts(products.slice(startIndex, endIndex));
  }, [products, currentPage]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = Object.fromEntries(searchParams);
      const response = await axios.get('/products', {
        params: { ...params, limit: 1000, page: 1 }
      });

      setProducts(response.data.data || []);
      const d = response.data.data || [];
      setTotalProducts(d.length);
    } catch (error) {
      console.error('Ürünler alınamadı:', error);
      setProducts([]);
      setDisplayedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategory = async (slug) => {
    try {
      const response = await axios.get(`/categories/slug/${slug}`);
      setCategory(response.data.data || null);
      fetchFilterOptions(slug);
    } catch (error) {
      console.error('Kategori adı alınamadı:', error);
      setCategory(null);
    }
  };

  const fetchCollection = async (slug) => {
    try {
      const response = await axios.get(`/collections/slug/${slug}`);
      setCollection(response.data.data || null);
    } catch (error) {
      console.error('Koleksiyon adı alınamadı:', error);
      setCollection(null);
    }
  };

  const fetchFilterOptions = async (slug) => {
    try {
      const response = await axios.get(`/categories/slug/${slug}/filter-options`);
      setFilterOptions(response.data.data || {
        materials: [],
        stoneTypes: [],
        genders: [],
        colors: []
      });
    } catch (error) {
      console.error('Filtre seçenekleri alınamadı:', error);
      setFilterOptions({
        materials: [],
        stoneTypes: [],
        genders: [],
        colors: []
      });
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilterImmediately = (name, value) => {
    const updatedFilters = { ...filters, [name]: value };
    setFilters(updatedFilters);
    
    const params = {};
    Object.keys(updatedFilters).forEach(key => {
      if (updatedFilters[key]) params[key] = updatedFilters[key];
    });
    
    delete params.page;
    setSearchParams(params);
    setCurrentPage(1);
  };

  const handleSortChange = (e) => {
    const sortValue = e.target.value;
    setFilters(prev => ({ ...prev, sort: sortValue }));
    const currentParams = Object.fromEntries(searchParams);
    const newParams = { ...currentParams, sort: sortValue };
    Object.keys(newParams).forEach(key => {
      if (!newParams[key]) delete newParams[key];
    });
    setSearchParams(newParams);
  };

  const applyFilters = () => {
    const params = {};
    Object.keys(filters).forEach(key => {
      if (filters[key]) params[key] = filters[key];
    });
    setSearchParams(params);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const clearFilters = () => {
    const categorySlug = searchParams.get('category');
    const reset = {
      keyword: '',
      category: categorySlug || '',
      minPrice: '',
      maxPrice: '',
      hasStone: '',
      material: '',
      stoneType: '',
      gender: '',
      color: '',
      carat: '',
      ringSizes: '',
      tags: '',
      type: '',
      sort: searchParams.get('sort') || '-createdAt',
    };
    setFilters(reset);
    if (categorySlug) {
      setSearchParams({ category: categorySlug });
    } else {
      setSearchParams({});
    }
    setCurrentPage(1);
    setShowFilters(false);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    const filterKeys = ['keyword', 'minPrice', 'maxPrice', 'hasStone', 'material', 'stoneType', 'gender', 'color', 'carat', 'ringSizes', 'tags', 'type'];
    filterKeys.forEach(key => {
      if (filters[key]) {
        count++;
      }
    });
    return count;
  };

  const renderActiveFilterChips = () => {
    const chips = [];
    
    if (filters.keyword) {
      chips.push({ key: 'keyword', label: `Arama: "${filters.keyword}"`, value: '' });
    }
    if (filters.minPrice || filters.maxPrice) {
      const label = filters.minPrice && filters.maxPrice
        ? `${filters.minPrice}₺ - ${filters.maxPrice}₺`
        : filters.minPrice
          ? `${filters.minPrice}₺+`
          : `${filters.maxPrice}₺-`;
      chips.push({ key: 'price', label, value: '', isDouble: true });
    }
    if (filters.hasStone) {
      const label = filters.hasStone === 'true' ? 'Taşlı' : 'Taşsız';
      chips.push({ key: 'hasStone', label: `Taş: ${label}`, value: '' });
    }
    if (filters.material) {
      const label = filters.material === 'rose' ? 'Rose Gold' : filters.material.charAt(0).toUpperCase() + filters.material.slice(1);
      chips.push({ key: 'material', label: `Materyal: ${label}`, value: '' });
    }
    if (filters.stoneType) {
      const label = filters.stoneType.charAt(0).toUpperCase() + filters.stoneType.slice(1);
      chips.push({ key: 'stoneType', label: `Taş Türü: ${label}`, value: '' });
    }
    if (filters.gender) {
      const label = filters.gender.charAt(0).toUpperCase() + filters.gender.slice(1);
      chips.push({ key: 'gender', label: `Cinsiyet: ${label}`, value: '' });
    }
    if (filters.carat) {
      chips.push({ key: 'carat', label: `${filters.carat} Ayar`, value: '' });
    }
    if (filters.ringSizes) {
      chips.push({ key: 'ringSizes', label: `Ölçü: ${filters.ringSizes}`, value: '' });
    }
    if (filters.tags) {
      const tagLabels = {
        'yeni': 'Yeni Ürünler',
        'indirimli': 'İndirimli Ürünler',
        'trend': 'Trend Ürünler',
        'çok-satan': 'En Çok Satanlar',
        'özel-tasarım': 'Özel Tasarım'
      };
      chips.push({ key: 'tags', label: tagLabels[filters.tags] || filters.tags, value: '' });
    }
    if (filters.type) {
      const typeLabels = {
        'yüzük': 'Yüzük',
        'alyans': 'Alyans',
        'kolye': 'Kolye',
        'zincir': 'Zincir',
        'gerdanlık': 'Gerdanlık',
        'bilezik': 'Bilezik',
        'bileklik': 'Bileklik',
        'kelepçe': 'Kelepçe',
        'küpe': 'Küpe',
        'halhal': 'Halhal',
        'şahmeran': 'Şahmeran',
        'set': 'Takı Seti'
      };
      filters.type.split(',').forEach(tVal => {
        if (tVal) {
          chips.push({ key: 'type-remove', label: typeLabels[tVal] || tVal, value: tVal });
        }
      });
    }

    if (chips.length === 0) return null;

    return (
      <div className="flex flex-wrap items-center gap-2 mt-4 max-w-[1400px] mx-auto px-1">
        <span className="text-xs text-gray-400 font-medium mr-1">Aktif Filtreler:</span>
        {chips.map((chip, idx) => (
          <div
            key={idx}
            className="flex items-center gap-1.5 bg-gray-50 border border-gray-200/80 rounded-full pl-3 pr-2 py-1 text-xs text-gray-700 transition-all hover:bg-gray-100 hover:border-gray-300 shadow-2xs"
          >
            <span>{chip.label}</span>
            <button
              type="button"
              onClick={() => {
                if (chip.isDouble) {
                  setFilters(prev => ({ ...prev, minPrice: '', maxPrice: '' }));
                  const updatedFilters = { ...filters, minPrice: '', maxPrice: '' };
                  const params = {};
                  Object.keys(updatedFilters).forEach(key => {
                    if (updatedFilters[key]) params[key] = updatedFilters[key];
                  });
                  setSearchParams(params);
                  setCurrentPage(1);
                } else if (chip.key === 'type-remove') {
                  const currentTypes = filters.type.split(',').filter(t => t !== chip.value);
                  applyFilterImmediately('type', currentTypes.join(','));
                } else {
                  applyFilterImmediately(chip.key, '');
                }
              }}
              className="text-gray-400 hover:text-red-500 font-bold ml-1 transition-colors flex items-center justify-center w-4 h-4 rounded-full hover:bg-gray-200/50"
            >
              <FiX className="text-[10px]" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={clearFilters}
          className="text-xs text-red-500 hover:text-red-600 font-semibold tracking-wide ml-2 hover:underline transition-all"
        >
          Tümünü Temizle
        </button>
      </div>
    );
  };

  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderCollapsibleSection = (key, title, content) => {
    const isExpanded = expandedSections[key];
    return (
      <div className="border-b border-gray-200 py-4">
        <button
          type="button"
          onClick={() => toggleSection(key)}
          className="flex items-center justify-between w-full text-left text-gray-900 font-semibold text-xs tracking-widest uppercase py-1 hover:text-[#45b59c] transition-colors"
        >
          <span>{title}</span>
          {isExpanded ? <FiChevronUp className="text-gray-500 text-sm" /> : <FiChevronDown className="text-gray-500 text-sm" />}
        </button>
        {isExpanded && (
          <div className="mt-4 space-y-3 animate-fadeIn">
            {content}
          </div>
        )}
      </div>
    );
  };

  const renderFilterContent = () => {
    return (
      <div className="space-y-6">
        {renderCollapsibleSection('search', t('nav.search', 'Arama'), (
          <div className="relative">
            <input
              type="text"
              name="keyword"
              value={filters.keyword}
              onChange={handleFilterChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyFilters();
              }}
              placeholder={t('products.searchPlaceholder')}
              className="w-full bg-white border border-gray-200 text-sm text-gray-600 pl-4 pr-10 py-2.5 focus:border-gray-500 outline-none rounded"
            />
            {filters.keyword ? (
              <button
                type="button"
                onClick={() => applyFilterImmediately('keyword', '')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FiX className="text-sm" />
              </button>
            ) : (
              <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm animate-pulse" />
            )}
          </div>
        ))}

        {renderCollapsibleSection('price', t('products.priceRange', 'Fiyat Aralığı'), (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="number"
                name="minPrice"
                value={filters.minPrice}
                onChange={handleFilterChange}
                placeholder="Min ₺"
                className="w-full bg-white border border-gray-200 text-sm text-gray-600 px-3 py-2 focus:border-gray-500 outline-none rounded text-center"
              />
              <span className="text-gray-400">—</span>
              <input
                type="number"
                name="maxPrice"
                value={filters.maxPrice}
                onChange={handleFilterChange}
                placeholder="Max ₺"
                className="w-full bg-white border border-gray-200 text-sm text-gray-600 px-3 py-2 focus:border-gray-500 outline-none rounded text-center"
              />
              <button
                type="button"
                onClick={applyFilters}
                className="bg-gray-900 text-white text-xs px-3 py-2.5 rounded hover:bg-gray-800 transition-colors"
              >
                {t('products.apply', 'Uygula')}
              </button>
            </div>
            
            <div className="flex flex-wrap gap-1.5 pt-1">
              {[
                { label: '0 - 2.5K ₺', min: '0', max: '2500' },
                { label: '2.5K - 5K ₺', min: '2500', max: '5000' },
                { label: '5K - 10K ₺', min: '5000', max: '10000' },
                { label: '10K+ ₺', min: '10000', max: '' }
              ].map(range => {
                const isSelected = filters.minPrice === range.min && filters.maxPrice === range.max;
                return (
                  <button
                    key={range.label}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        setFilters(prev => ({ ...prev, minPrice: '', maxPrice: '' }));
                        const updated = { ...filters, minPrice: '', maxPrice: '' };
                        const params = {};
                        Object.keys(updated).forEach(k => { if (updated[k]) params[k] = updated[k]; });
                        setSearchParams(params);
                      } else {
                        setFilters(prev => ({ ...prev, minPrice: range.min, maxPrice: range.max }));
                        const updated = { ...filters, minPrice: range.min, maxPrice: range.max };
                        const params = {};
                        Object.keys(updated).forEach(k => { if (updated[k]) params[k] = updated[k]; });
                        setSearchParams(params);
                      }
                    }}
                    className={`text-[11px] px-2.5 py-1 rounded transition-colors ${
                      isSelected 
                        ? 'bg-gray-900 text-white font-medium' 
                        : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-900 border border-gray-100'
                    }`}
                  >
                    {range.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {renderCollapsibleSection('type', t('products.productType', 'Ürün Tipi'), (
          <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar pr-1">
            {[
              { label: 'Yüzük', val: 'yüzük' },
              { label: 'Alyans', val: 'alyans' },
              { label: 'Kolye', val: 'kolye' },
              { label: 'Zincir', val: 'zincir' },
              { label: 'Gerdanlık', val: 'gerdanlık' },
              { label: 'Bilezik', val: 'bilezik' },
              { label: 'Bileklik', val: 'bileklik' },
              { label: 'Kelepçe', val: 'kelepçe' },
              { label: 'Küpe', val: 'küpe' },
              { label: 'Halhal', val: 'halhal' },
              { label: 'Şahmeran', val: 'şahmeran' },
              { label: 'Takı Seti', val: 'set' }
            ].map(opt => {
              const isSelected = filters.type.split(',').includes(opt.val);
              return (
                <label key={opt.val} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {
                      let currentTypes = filters.type ? filters.type.split(',') : [];
                      if (isSelected) {
                        currentTypes = currentTypes.filter(t => t !== opt.val);
                      } else {
                        currentTypes.push(opt.val);
                      }
                      applyFilterImmediately('type', currentTypes.join(','));
                    }}
                    className="accent-gray-900 w-4 h-4 cursor-pointer rounded"
                  />
                  <span className={`text-sm transition-colors ${isSelected ? 'text-gray-950 font-medium' : 'text-gray-500 group-hover:text-gray-900'}`}>
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>
        ))}

        {renderCollapsibleSection('gender', t('products.gender', 'Cinsiyet'), (
          <div className="space-y-2">
            {[
              { label: t('products.all', 'Tümü'), val: '' },
              { label: 'Kadın', val: 'kadın' },
              { label: 'Erkek', val: 'erkek' },
              { label: 'Unisex', val: 'unisex' }
            ].map(opt => (
              <label key={opt.val} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="gender"
                  value={opt.val}
                  checked={filters.gender === opt.val}
                  onChange={(e) => applyFilterImmediately('gender', e.target.value)}
                  className="accent-gray-900 w-4 h-4 cursor-pointer"
                />
                <span className={`text-sm transition-colors ${filters.gender === opt.val ? 'text-gray-950 font-medium' : 'text-gray-500 group-hover:text-gray-900'}`}>
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        ))}

        {renderCollapsibleSection('material', t('products.material', 'Materyal'), (
          <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-1">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="radio"
                name="material"
                value=""
                checked={filters.material === ''}
                onChange={(e) => applyFilterImmediately('material', '')}
                className="accent-gray-900 w-4 h-4 cursor-pointer"
              />
              <span className={`text-sm transition-colors ${filters.material === '' ? 'text-gray-950 font-medium' : 'text-gray-500 group-hover:text-gray-900'}`}>
                {t('products.all')}
              </span>
            </label>
            {['altın', 'rose', 'gümüş', 'platin', 'çelik', 'pırlanta'].map(mat => {
              const displayMat = mat.charAt(0).toUpperCase() + mat.slice(1);
              const isSelected = filters.material.toLowerCase() === mat;
              return (
                <label key={mat} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="material"
                    value={mat}
                    checked={isSelected}
                    onChange={(e) => applyFilterImmediately('material', e.target.value)}
                    className="accent-gray-900 w-4 h-4 cursor-pointer"
                  />
                  <span className={`text-sm transition-colors ${isSelected ? 'text-gray-950 font-medium' : 'text-gray-500 group-hover:text-gray-900'}`}>
                    {displayMat === 'Rose' ? 'Rose Gold' : displayMat}
                  </span>
                </label>
              );
            })}
          </div>
        ))}

        {renderCollapsibleSection('stone', t('products.hasStone', 'Taş Detayı'), (
          <div className="space-y-4">
            <div>
              <span className="text-[10px] text-gray-400 font-semibold tracking-wider block mb-2 uppercase">Taş Durumu</span>
              <div className="space-y-2">
                {[
                  { label: t('products.all'), val: '' },
                  { label: 'Taşlı', val: 'true' },
                  { label: 'Taşsız', val: 'false' }
                ].map(opt => (
                  <label key={opt.val} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="hasStone"
                      value={opt.val}
                      checked={filters.hasStone === opt.val}
                      onChange={(e) => applyFilterImmediately('hasStone', e.target.value)}
                      className="accent-gray-900 w-4 h-4 cursor-pointer"
                    />
                    <span className={`text-sm transition-colors ${filters.hasStone === opt.val ? 'text-gray-950 font-medium' : 'text-gray-500 group-hover:text-gray-900'}`}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {filters.hasStone !== 'false' && (
              <div className="border-t border-gray-100 pt-3">
                <span className="text-[10px] text-gray-400 font-semibold tracking-wider block mb-2 uppercase">{t('products.stoneType', 'Taş Türü')}</span>
                <div className="space-y-2 max-h-36 overflow-y-auto custom-scrollbar pr-1">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="radio"
                      name="stoneType"
                      value=""
                      checked={filters.stoneType === ''}
                      onChange={(e) => applyFilterImmediately('stoneType', '')}
                      className="accent-gray-900 w-4 h-4 cursor-pointer"
                    />
                    <span className={`text-sm transition-colors ${filters.stoneType === '' ? 'text-gray-950 font-medium' : 'text-gray-500 group-hover:text-gray-900'}`}>
                      Tümü
                    </span>
                  </label>
                  {['pırlanta', 'zirkon', 'yakut', 'zümrüt', 'safir', 'doğal taş', 'inci', 'ametist', 'kuvars', 'kehribar', 'akik', 'sedef', 'turkuaz'].map(type => {
                    const displayType = type === 'doğal taş' ? 'Doğal Taş' : type.charAt(0).toUpperCase() + type.slice(1);
                    const isSelected = filters.stoneType.toLowerCase() === type;
                    return (
                      <label key={type} className="flex items-center gap-3 cursor-pointer group">
                        <input
                          type="radio"
                          name="stoneType"
                          value={type}
                          checked={isSelected}
                          onChange={(e) => applyFilterImmediately('stoneType', e.target.value)}
                          className="accent-gray-900 w-4 h-4 cursor-pointer"
                        />
                        <span className={`text-sm transition-colors ${isSelected ? 'text-gray-950 font-medium' : 'text-gray-500 group-hover:text-gray-900'}`}>
                          {displayType}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}

        {renderCollapsibleSection('carat', t('product.details.carat', 'Altın Ayarı'), (
          <div className="space-y-2">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="radio"
                name="carat"
                value=""
                checked={filters.carat === ''}
                onChange={(e) => applyFilterImmediately('carat', '')}
                className="accent-gray-900 w-4 h-4 cursor-pointer"
              />
              <span className={`text-sm transition-colors ${filters.carat === '' ? 'text-gray-950 font-medium' : 'text-gray-500 group-hover:text-gray-900'}`}>
                Tümü
              </span>
            </label>
            {['8', '14', '18', '22'].map(carat => {
              const isSelected = filters.carat === carat;
              return (
                <label key={carat} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="carat"
                    value={carat}
                    checked={isSelected}
                    onChange={(e) => applyFilterImmediately('carat', e.target.value)}
                    className="accent-gray-900 w-4 h-4 cursor-pointer"
                  />
                  <span className={`text-sm transition-colors ${isSelected ? 'text-gray-950 font-medium' : 'text-gray-500 group-hover:text-gray-900'}`}>
                    {carat} Ayar
                  </span>
                </label>
              );
            })}
          </div>
        ))}

        {renderCollapsibleSection('ringSize', t('product.ringSize.label', 'Yüzük Ölçüsü'), (
          <div className="grid grid-cols-4 gap-2 pr-1">
            {['8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24'].map(size => {
              const isSelected = filters.ringSizes === size;
              return (
                <button
                  key={size}
                  type="button"
                  onClick={() => applyFilterImmediately('ringSizes', isSelected ? '' : size)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold tracking-wide border transition-all ${
                    isSelected
                      ? 'bg-gray-900 text-white border-gray-900 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  {size}
                </button>
              );
            })}
          </div>
        ))}

        {renderCollapsibleSection('tags', t('footer.newsletter', 'Koleksiyon & Etiket'), (
          <div className="space-y-2">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="radio"
                name="tags"
                value=""
                checked={filters.tags === ''}
                onChange={(e) => applyFilterImmediately('tags', '')}
                className="accent-gray-900 w-4 h-4 cursor-pointer"
              />
              <span className={`text-sm transition-colors ${filters.tags === '' ? 'text-gray-950 font-medium' : 'text-gray-500 group-hover:text-gray-900'}`}>
                Tümü
              </span>
            </label>
            {[
              { label: 'Yeni Ürünler', val: 'yeni' },
              { label: 'İndirimli Ürünler', val: 'indirimli' },
              { label: 'Trend Ürünler', val: 'trend' },
              { label: 'En Çok Satanlar', val: 'çok-satan' },
              { label: 'Özel Tasarım', val: 'özel-tasarım' }
            ].map(tag => {
              const isSelected = filters.tags === tag.val;
              return (
                <label key={tag.val} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="tags"
                    value={tag.val}
                    checked={isSelected}
                    onChange={(e) => applyFilterImmediately('tags', e.target.value)}
                    className="accent-gray-900 w-4 h-4 cursor-pointer"
                  />
                  <span className={`text-sm transition-colors ${isSelected ? 'text-gray-950 font-medium' : 'text-gray-500 group-hover:text-gray-900'}`}>
                    {tag.label}
                  </span>
                </label>
              );
            })}
          </div>
        ))}

        <div className="pt-4 flex gap-2 hidden lg:flex">
          <button 
            type="button"
            onClick={clearFilters} 
            className="w-full border border-gray-300 text-gray-600 py-3 text-sm hover:text-gray-900 transition-colors hover:bg-gray-50 rounded font-semibold tracking-wider"
          >
            {t('products.clear', 'Filtreleri Temizle').toUpperCase()}
          </button>
        </div>
      </div>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);
    
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border text-xs md:text-sm font-medium transition-all duration-300 rounded-full shrink-0 ${
            currentPage === i
              ? 'bg-[var(--primary-color)] text-white border-[var(--primary-color)] shadow-sm'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex items-center justify-center gap-1.5 md:gap-2 my-12 flex-wrap px-4">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border border-gray-200 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 hover:text-gray-900 transition-all duration-300 rounded-full shrink-0"
          aria-label={t('products.previous', 'Önceki')}
        >
          <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        {startPage > 1 && (
          <>
            <button
              onClick={() => handlePageChange(1)}
              className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900 text-xs md:text-sm font-medium transition-all duration-300 rounded-full shrink-0"
            >
              1
            </button>
            {startPage > 2 && <span className="px-1 text-gray-400 self-center">...</span>}
          </>
        )}
        {pages}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-1 text-gray-400 self-center">...</span>}
            <button
              onClick={() => handlePageChange(totalPages)}
              className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900 text-xs md:text-sm font-medium transition-all duration-300 rounded-full shrink-0"
            >
              {totalPages}
            </button>
          </>
        )}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border border-gray-200 bg-white text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 hover:text-gray-900 transition-all duration-300 rounded-full shrink-0"
          aria-label={t('products.next', 'Sonraki')}
        >
          <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    );
  };

  if (loading && !products.length) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center gap-4 animate-fadeIn">
        <div className="font-serif text-3xl font-light tracking-[0.3em] text-gray-900 animate-pulse">MASHOW</div>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const activeBannerItem = collection || category;
  const bannerImage = activeBannerItem?.image 
    ? getCategoryImage(activeBannerItem.image) 
    : "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?auto=format&fit=crop&q=75&w=1200";
  const bannerTitle = activeBannerItem 
    ? getLocalized(activeBannerItem, 'name', i18n.language) 
    : (filters.keyword ? `"${filters.keyword}" ${t('products.searchResults')}` : t('products.allProducts'));
  const bannerDesc = activeBannerItem ? getLocalized(activeBannerItem, 'description', i18n.language) : null;

  return (
    <div className="min-h-screen bg-white text-gray-600 animate-fadeIn">
      {/* Hero Banner for Collections, Categories & Search */}
      <div className="relative w-full h-[220px] sm:h-[300px] md:h-[380px] overflow-hidden mb-4 bg-gray-950 flex items-center justify-center">
        <img
          src={bannerImage}
          alt={bannerTitle}
          className="absolute inset-0 w-full h-full object-cover opacity-40 transform hover:scale-105 transition-transform duration-[4000ms] ease-out"
          loading="eager"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
        <div className="relative z-10 text-center px-4 max-w-[850px] animate-fadeIn">
          <span className="block text-[#45b59c] text-[10px] md:text-[11px] tracking-[0.35em] uppercase font-bold mb-3 sm:mb-4">
            MASHOW COLLECTION
          </span>
          <h1 className="font-serif text-2xl sm:text-4xl md:text-5xl font-light tracking-[0.2em] text-white uppercase mb-3 sm:mb-4 drop-shadow-md">
            {bannerTitle}
          </h1>
          {bannerDesc && (
            <p className="text-white/90 text-[11px] sm:text-xs md:text-sm font-light tracking-widest leading-relaxed max-w-2xl mx-auto drop-shadow-sm line-clamp-2">
              {bannerDesc}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-12">
        {renderActiveFilterChips()}
      </div>

      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-8 flex flex-col lg:flex-row gap-8">
        
        {/* SIDEBAR (Desktop) */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-24 space-y-8 pr-4">
            <div className="flex items-center gap-3 text-gray-900 font-semibold tracking-wider text-sm border-b border-gray-200 pb-4">
              <FiFilter className="text-[#45b59c]" /> FİLTRELE
            </div>
            {renderFilterContent()}
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between border-b border-gray-200 pb-3 md:pb-4 mb-4 md:mb-6">
            <div className="text-xs md:text-sm text-gray-500">{t('products.itemCount', { count: totalProducts })}</div>
            
            <div className="hidden lg:flex items-center gap-4">
              <select
                name="sort"
                value={filters.sort}
                onChange={handleSortChange}
                className="bg-white border border-gray-200 text-gray-600 text-sm px-4 py-2 outline-none cursor-pointer focus:border-gray-500 rounded"
              >
                <option value="-createdAt" className="bg-white">{t('products.defaultSort')}</option>
                <option value="price" className="bg-white">{t('products.sort.priceLowHigh')}</option>
                <option value="-price" className="bg-white">{t('products.sort.priceHighLow')}</option>
                <option value="name" className="bg-white">{t('products.sort.nameAZ')}</option>
              </select>
            </div>
          </div>

          {/* Grid */}
          {displayedProducts.length === 0 ? (
            (filters.category || category) && getActiveFilterCount() === 0 ? (
              <FadeIn className="text-center py-24 px-6 border border-gray-100 rounded-lg bg-gray-50/50 flex flex-col items-center justify-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 mb-2">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="font-serif text-2xl text-gray-900 font-light tracking-[0.1em] uppercase">
                  {t('products.underMaintenanceTitle')}
                </h3>
                <p className="text-sm text-gray-500 font-light max-w-md mx-auto leading-relaxed">
                  {t('products.underMaintenanceDesc')}
                </p>
                <div className="w-12 h-[1px] bg-primary-200 my-4"></div>
                <button
                  type="button"
                  onClick={() => setSearchParams({})}
                  className="inline-flex items-center justify-center border border-gray-900 text-gray-900 px-8 py-3 text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-gray-900 hover:text-white transition-all duration-300 rounded-sm"
                >
                  {t('products.allProducts')}
                </button>
              </FadeIn>
            ) : (
              <FadeIn className="text-center py-20 border border-gray-200 rounded-lg">
                <p className="text-gray-500 font-light tracking-wide">{t('products.noProducts')}</p>
              </FadeIn>
            )
          ) : (
            <>
              <StaggerContainer key={`grid-${currentPage}-${searchParams.toString()}`} viewport={null} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 md:gap-6">
                {displayedProducts.map((product) => (
                  <StaggerItem key={product._id}>
                    <ProductCard product={product} />
                  </StaggerItem>
                ))}
              </StaggerContainer>

              {/* Bottom Pagination */}
              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {/* Sticky Floating Bottom Bar for Mobile (Left Aligned) */}
      <div className="lg:hidden fixed bottom-8 left-6 z-40 bg-white/95 backdrop-blur-md border border-gray-200/80 shadow-[0_8px_24px_rgba(0,0,0,0.12)] rounded-full flex divide-x divide-gray-100 py-2.5 px-4 items-center justify-start gap-1 animate-slideUp w-fit">
        <button
          type="button"
          onClick={() => setShowFilters(true)}
          className="flex items-center justify-center gap-1.5 text-gray-900 text-[12px] font-bold tracking-wide uppercase active:scale-95 transition-all pr-2.5"
        >
          <FiFilter className="text-[#45b59c] text-base" /> 
          <span>Filtrele</span>
          {getActiveFilterCount() > 0 && (
            <span className="bg-[#45b59c] text-white text-[9px] w-4.5 h-4.5 rounded-full flex items-center justify-center font-bold">
              {getActiveFilterCount()}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setShowSortModal(true)}
          className="flex items-center justify-center gap-1 text-gray-900 text-[12px] font-bold tracking-wide uppercase active:scale-95 transition-all pl-2.5"
        >
          <span>Sırala</span>
          <FiChevronUp className="text-gray-400 rotate-180 text-sm" />
        </button>
      </div>

      {/* Sort Bottom Sheet (Mobile) */}
      {showSortModal && (
        <div className="fixed inset-0 z-50 bg-black/45 backdrop-blur-xs flex items-end justify-center lg:hidden transition-all duration-300">
          <div className="absolute inset-0" onClick={() => setShowSortModal(false)}></div>
          <div className="relative bg-white w-full rounded-t-2xl max-h-[80vh] overflow-y-auto p-6 z-10 animate-slideUp shadow-2xl">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h3 className="text-gray-900 font-serif text-lg tracking-wide">{t('products.sortTitle', 'Sırala')}</h3>
              <button onClick={() => setShowSortModal(false)} className="text-gray-400 hover:text-gray-600">
                <FiX className="text-xl" />
              </button>
            </div>
            <div className="space-y-3">
              {[
                { label: t('products.defaultSort', 'Varsayılan Sıralama'), val: '-createdAt' },
                { label: t('products.sort.priceLowHigh', 'Fiyat: Düşükten Yükseğe'), val: 'price' },
                { label: t('products.sort.priceHighLow', 'Fiyat: Yüksekten Düşüğe'), val: '-price' },
                { label: t('products.sort.nameAZ', 'Ürün Adı: A-Z'), val: 'name' }
              ].map(opt => (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => {
                    setFilters(prev => ({ ...prev, sort: opt.val }));
                    const currentParams = Object.fromEntries(searchParams);
                    const newParams = { ...currentParams, sort: opt.val };
                    Object.keys(newParams).forEach(key => {
                      if (!newParams[key]) delete newParams[key];
                    });
                    setSearchParams(newParams);
                    setShowSortModal(false);
                  }}
                  className={`w-full text-left py-3.5 px-4 rounded-lg flex items-center justify-between text-sm transition-all ${
                    filters.sort === opt.val
                      ? 'bg-gray-900 text-white font-medium shadow-sm'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span>{opt.label}</span>
                  {filters.sort === opt.val && <span className="w-2 h-2 bg-white rounded-full"></span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Filters Drawer Overlay */}
      {showFilters && (
        <div className="fixed inset-0 z-50 bg-black/45 backdrop-blur-xs flex justify-start lg:hidden transition-all duration-300">
          <div className="absolute inset-0" onClick={() => setShowFilters(false)}></div>
          <div className="relative bg-white w-[88vw] sm:w-[420px] h-full flex flex-col z-10 shadow-2xl animate-slideInLeft">
            
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <h2 className="text-gray-900 font-serif text-lg tracking-wide font-medium">{t('products.filters', 'Filtreler')}</h2>
                {getActiveFilterCount() > 0 && (
                  <span className="bg-[#45b59c] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {getActiveFilterCount()}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {getActiveFilterCount() > 0 && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-xs text-red-500 hover:text-red-600 font-semibold tracking-wide"
                  >
                    Temizle
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <FiX className="text-2xl" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-6 custom-scrollbar pb-24">
              {renderFilterContent()}
            </div>

            {/* Sticky Footer */}
            <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 flex gap-3 z-10 shadow-[0_-8px_24px_rgba(0,0,0,0.05)]">
              <button
                type="button"
                onClick={clearFilters}
                className="flex-1 border border-gray-200 text-gray-700 py-3 px-4 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 active:bg-gray-100 transition-all rounded"
              >
                {t('products.clear', 'Temizle')}
              </button>
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                className="flex-[2] bg-gray-900 text-white py-3 px-4 text-xs font-bold uppercase tracking-wider hover:bg-gray-800 active:bg-black transition-all rounded shadow-md flex items-center justify-center gap-2"
              >
                <span>{t('products.showResults', 'Uygula')}</span>
                <span className="bg-white/20 text-white px-2 py-0.5 rounded text-[10px] font-sans font-bold">
                  {totalProducts}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
